import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import i18n from 'app/config/i18Config/i18n';
import { AppLanguage } from 'app/config/i18Config/types';
import { APP_LANGUAGE_KEY, languageToLabelMap, languageToShortLabelMap } from 'shared/utils/constants';
import { getLoggedUserData, useChangeUserDetailsMutation } from 'entities/User';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState/model/selectors/getGlobalSettings';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { Select } from 'shared/ui/Select';
import s from './LangSwitcher.module.scss';

interface LangSwitcherProps {
  // Applies only on mobile; defaults to bg-backgroundColor
  mobileBackgroundClassName?: string;
  // If provided, inverts behavior: when true, render MOBILE version (even on desktop)
  isDesktop?: boolean;
}

export const LangSwitcher: FC<LangSwitcherProps> = memo((props) => {
  const { mobileBackgroundClassName = 'bg-backgroundColor', isDesktop: isDesktopProp } = props;
  const [language, setLanguage] = useState<AppLanguage>(AppLanguage.ru);

  const loggedUser = useAppSelector(getLoggedUserData);
  const globalSettings = useAppSelector(getGlobalSettings);

  const [changeUserLanguage] = useChangeUserDetailsMutation();

  const { isDesktop: hookIsDesktop } = useScreenBreakpoints();
  // If isDesktop prop is provided and true, we intentionally render MOBILE UI
  // Otherwise we fallback to screen size detection
  const renderMobileUI = isDesktopProp ?? !hookIsDesktop;

  const handleLanguageChange = useCallback(
    async (language: AppLanguage): Promise<void> => {
      await i18n.changeLanguage(language);

      if (loggedUser && loggedUser.language !== language) {
        await changeUserLanguage({ userId: loggedUser.userId, params: { language } });
      }

      localStorage.setItem(APP_LANGUAGE_KEY, language);

      setLanguage(language);
    },
    [changeUserLanguage, loggedUser],
  );

  const initializeLanguage = useCallback(async (): Promise<void> => {
    if (loggedUser?.language) {
      await handleLanguageChange(loggedUser.language as AppLanguage);
    } else {
      const storedLanguage = localStorage.getItem(APP_LANGUAGE_KEY) as AppLanguage;

      if (Object.values(AppLanguage).includes(storedLanguage)) {
        setLanguage(storedLanguage);
      }
    }
  }, [handleLanguageChange, loggedUser?.language]);

  useEffect(() => {
    void initializeLanguage();
  }, [initializeLanguage]);

  const appLanguages =
    globalSettings?.availableLanguages
      .filter((lang: string): lang is AppLanguage => Object.values(AppLanguage).includes(lang as AppLanguage))
      .map((language: AppLanguage) => ({
        label: renderMobileUI ? languageToShortLabelMap[language] : languageToLabelMap[language],
        value: language,
      })) || [];

  const handleSelectChange = useCallback(
    (value: string): void => {
      void handleLanguageChange(value as AppLanguage);
    },
    [handleLanguageChange],
  );

  return (
    <div id="container" className={classNames({ [mobileBackgroundClassName]: renderMobileUI })}>
      <Select
        options={appLanguages}
        size="small"
        bordered={!renderMobileUI}
        background={mobileBackgroundClassName === 'bg-backgroundColor' ? 'background' : 'white'}
        compact={renderMobileUI}
        className={classNames(s.selectOverride, { [s.mobileSelect]: renderMobileUI })}
        value={language}
        onChange={handleSelectChange}
        showSuffixIcon={!renderMobileUI}
        popupMatchSelectWidth={!renderMobileUI}
      />
    </div>
  );
});
